import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { CollapsibleState } from "~/components/build/Show/components/Sidebar/TreeItem/CollapsibleState";
import {
  TreeNode,
  TreeNodeRenderProps,
  TreeNodeType,
} from "~/components/build/Show/components/Sidebar/TreeNode";
import { CollapsibleStepItem } from "~/components/build/Show/components/Sidebar/TreeNode/CollapsibleStepItem";
import { createCommandJobNode } from "~/components/build/Show/components/Sidebar/TreeNode/CommandJobItem";
import { createShowHiddenJobsNode } from "~/components/build/Show/components/Sidebar/TreeNode/CommandStepExpandItem";
import { filterStepJobs } from "~/components/build/Show/components/Sidebar/useFilterStore";
import { useBuild, mostRecentMatrixJobs } from "~/components/build/Show/lib/BuildContext";
import { fetchBuildStepJobs } from "~/components/build/Show/lib/queries";
import { sortJobsByState } from "~/components/build/Show/lib/sortJobsByState";
import { Build, CommandJob } from "~/components/build/Show/lib/types";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { CommandStep } from "~/lib/pipeline";

const MatrixStepItem = ({ step, ...props }: { step: CommandStep } & TreeNodeRenderProps) => {
  const { build } = useBuild();
  const setJobs = useBuildStore((state) => state.setJobs);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["build", build.number, "steps", step.uuid, "jobs"],
    queryFn: () => fetchBuildStepJobs({ stepUuid: step.uuid, ...build }),
    enabled:
      Features.ProgressiveBuildShow &&
      (props.collapsibleState === CollapsibleState.Expanded ||
        props.collapsibleState === CollapsibleState.Full),
  });

  useEffect(() => {
    if (query.data) {
      setJobs(query.data);

      query.data.map((job) => {
        // Pre-populate query cache for jobs/:id
        queryClient.setQueryData(["build", build.number, "jobs", job.id], { job });
      });
    }
  }, [query.dataUpdatedAt]);

  return <CollapsibleStepItem {...props} step={step} isLoading={query.isLoading} />;
};

export const createMatrixStepNode = (step: CommandStep, build: Build): TreeNode => ({
  id: step.uuid,
  type: TreeNodeType.StepCommandMatrix,
  render: (props) => <MatrixStepItem step={step} {...props} />,
  childNodes: (collapsibleState: CollapsibleState = CollapsibleState.Collapsed) => {
    if (collapsibleState === CollapsibleState.Collapsed) {
      return [];
    }

    const jobs = mostRecentMatrixJobs(
      (useBuildStore.getState().stepJobs.get(step.uuid) || []) as CommandJob[],
    );

    const items: TreeNode[] = [];

    if (jobs.length > 0) {
      const filteredJobs =
        collapsibleState === CollapsibleState.Full ? [...jobs] : filterStepJobs(jobs, step, build);

      filteredJobs.sort(sortJobsByState("asc"));

      for (const job of filteredJobs) {
        items.push(createCommandJobNode(step, job as CommandJob));
      }

      const hiddenCount = jobs.length - filteredJobs.length;
      if (hiddenCount > 0) {
        items.push(createShowHiddenJobsNode(step, hiddenCount));
      }
    }

    return items;
  },
});
